import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  AlertTriangle, 
  Search,
  FileText,
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProposalTemplate, TemplateType } from '@/types/business-opportunities';
import { getProposalTemplates } from '@/services/business-opportunities';
import { supabase } from '@/lib/supabase';
import { sanitizeTemplateHTML } from '@/utils/htmlSanitizer';

// Template parcial para listar, sem campos obrigatórios do BaseEntity
type DisplayableTemplate = Omit<Partial<ProposalTemplate>, 'created_by' | 'updated_by'> & {
  id: string;
  name: string;
  description?: string;
  template_type: TemplateType;
  html_content: string;
  css_content?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const ProposalTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [templates, setTemplates] = useState<DisplayableTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateTypeFilter, setTemplateTypeFilter] = useState<string>('_all');
  const [statusFilter, setStatusFilter] = useState<string>('_all');
  
  // Modais
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DisplayableTemplate | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  
  // Tipos de templates disponíveis (de acordo com o enum TemplateType)
  const templateTypes = [
    { value: 'services' as TemplateType, label: 'Serviço' },
    { value: 'products' as TemplateType, label: 'Produto' },
    { value: 'licensing' as TemplateType, label: 'Licenciamento' },
    { value: 'consultancy' as TemplateType, label: 'Consultoria' },
    { value: 'subscription' as TemplateType, label: 'Assinatura' }
  ];
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      
      const data = await getProposalTemplates();
      
      if (data) {
        // Convert the API response to our local template type
        const templates: DisplayableTemplate[] = data.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          template_type: item.template_type as TemplateType,
          html_content: item.template_html || '', // Note: mapping from DB field to interface field
          css_content: item.css_content || '',
          is_active: item.is_active,
          created_at: item.created_at,
          updated_at: item.updated_at,
          organization_id: item.organization_id
        }));
        
        setTemplates(templates);
      }
    } catch (error) {
      console.error('Erro ao buscar templates de proposta:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os templates de proposta.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteClick = (template: DisplayableTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };
  
  const handleViewClick = (template: DisplayableTemplate) => {
    setSelectedTemplate(template);
    setPreviewHtml(template.html_content);
    setShowViewDialog(true);
  };
  
  const handleEditClick = (template: DisplayableTemplate) => {
    navigate(`/proposal-templates/${template.id}`);
  };
  
  const handleCreateTemplate = () => {
    navigate('/proposal-templates/new');
  };
  
  const confirmDelete = async () => {
    if (!selectedTemplate) return;
    
    try {
      // Usando supabase diretamente
      const { error } = await supabase
        .from('proposal_templates')
        .delete()
        .eq('id', selectedTemplate.id);
        
      if (error) throw error;
      
      // Atualizar a lista local
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
      
      toast({
        title: 'Template excluído',
        description: 'O template de proposta foi excluído com sucesso.',
      });
      
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o template de proposta.',
        variant: 'destructive',
      });
    }
  };
  
  // Filtragem de templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
      
    const matchesType = templateTypeFilter === '_all' || templateTypeFilter === template.template_type;
    const matchesStatus = statusFilter === '_all' || 
      (statusFilter === 'active' && template.is_active) ||
      (statusFilter === 'inactive' && !template.is_active);
      
    return matchesSearch && matchesType && matchesStatus;
  });
  
  // Obter o rótulo para um tipo de template
  const getTemplateTypeLabel = (type: string) => {
    const templateType = templateTypes.find(t => t.value === type);
    return templateType ? templateType.label : type;
  };
  
  // Preview do template HTML
  const handlePreviewTemplate = (template: DisplayableTemplate) => {
    setPreviewHtml(template.html_content);
    setShowPreviewDialog(true);
  };
  
  return (
    <div className="container mx-auto p-4">
      <Card className="w-full">
        <CardHeader className="bg-card">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-extrabold">Templates de Proposta</CardTitle>
              <CardDescription>
                Gerenciar templates de proposta para diferentes tipos de oportunidades de negócio
              </CardDescription>
            </div>
            <Button 
              onClick={handleCreateTemplate} 
              className="bg-primary text-white hover:bg-primary/90"
            >
              <Plus className="mr-2 h-4 w-4" /> Novo Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="flex flex-wrap gap-4 mb-6 mt-2">
            <div className="flex-1 min-w-[240px]">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar templates..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Select
                value={templateTypeFilter}
                onValueChange={setTemplateTypeFilter}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Tipo de Template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos os tipos</SelectItem>
                  {templateTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos os status</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabela de Templates */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center p-6 border border-dashed rounded-lg my-4">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-2 text-lg font-extrabold">Nenhum template encontrado</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery || templateTypeFilter !== '_all' || statusFilter !== '_all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece criando seu primeiro template de proposta'}
              </p>
              <Button onClick={handleCreateTemplate} variant="outline">
                <Plus className="mr-2 h-4 w-4" /> Criar Template
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="w-[30%]">Nome</TableHead>
                    <TableHead className="w-[35%]">Descrição</TableHead>
                    <TableHead className="w-[15%]">Tipo</TableHead>
                    <TableHead className="w-[10%]">Status</TableHead>
                    <TableHead className="w-[10%] text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-extrabold">{template.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {template.description || 'Sem descrição'}
                      </TableCell>
                      <TableCell>{getTemplateTypeLabel(template.template_type)}</TableCell>
                      <TableCell>
                        {template.is_active ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            Inativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleViewClick(template)}
                          title="Visualizar template"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEditClick(template)}
                          title="Editar template"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteClick(template)}
                          title="Excluir template"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> 
              Confirmar exclusão
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o template "{selectedTemplate?.name}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização do HTML */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description || 'Sem descrição'}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded p-4 max-h-[400px] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: sanitizeTemplateHTML(previewHtml) }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => handleEditClick(selectedTemplate!)}>
              Editar Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Prévia */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Prévia do Template</DialogTitle>
          </DialogHeader>
          <div className="border rounded-md p-4 overflow-auto max-h-[calc(90vh-120px)]">
            <div dangerouslySetInnerHTML={{ __html: sanitizeTemplateHTML(previewHtml) }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalTemplates; 